// Kommunicate: pre-hide host iframe until onInit + theme CSS, or at most `concealMaxMs`.
// `window.Kommunicate` is from CDN, not a module import.

import chatbotAvatarUrl from './assets/Chatbot.svg'
import greetingBubbleUrl from './assets/Chat_Bubble 1.svg'
import closeButtonUrl from './assets/Close_Button.svg'
import {
  KOMMUNICATE_SCRIPT_URL,
  RESOURCE_LINKS,
  RESOURCE_STRIP_ID,
  SELECTORS,
  SPEAK_TO_PERSON_BUTTON_ID,
  resolveConfig,
} from './config.js'
import { buildWidgetCss } from './widget-css.js'
import {
  configureAnalytics,
  getAnalyticsSessionId,
  getAnonymousUserId,
  newClientActionId,
  track,
} from './analytics/index.js'

const INIT_FLAG = '__finteplaKommunicateInitialized'
const LOADING_CLASS = 'fintepla-kommunicate-loading'
const PREHIDE_STYLE_ID = 'fintepla-kommunicate-prehide'
const READY_TIMEOUT_MS = 15000

let widgetCssBundleForKommunicate = ''

function debugLog(config, ...args) {
  if (config.debug) {
    console.info('[Fintepla]', ...args)
  }
}

function installPrehideStyle() {
  if (document.getElementById(PREHIDE_STYLE_ID)) {
    document.documentElement.classList.add(LOADING_CLASS)
    return
  }

  const style = document.createElement('style')
  style.id = PREHIDE_STYLE_ID
  // Broad selectors: Kommunicate's iframe element id/name has varied across
  // SDK versions, so we hide anything that looks like a Kommunicate iframe
  // AND the documented sidebox IDs as belt-and-braces.
  style.textContent = `
    html.${LOADING_CLASS} iframe[src*="kommunicate.io"],
    html.${LOADING_CLASS} iframe[src*="kommunicate.app"],
    html.${LOADING_CLASS} iframe[id^="km-"],
    html.${LOADING_CLASS} iframe[id^="mck-"],
    html.${LOADING_CLASS} iframe[name*="kommunicate" i],
    html.${LOADING_CLASS} #mck-sidebox-launcher,
    html.${LOADING_CLASS} #mck-sidebox {
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
  `
  document.head.appendChild(style)
  document.documentElement.classList.add(LOADING_CLASS)
}

function revealWidget(config, reason = 'onInit') {
  if (!document.documentElement.classList.contains(LOADING_CLASS)) {
    // Already revealed — no-op.
    return
  }
  document.documentElement.classList.remove(LOADING_CLASS)
  document.getElementById(PREHIDE_STYLE_ID)?.remove()

  // If concealMaxMs fires before onInit, the iframe can paint once with SDK
  // defaults unless we inject theme CSS before displayKommunicateWidget.
  try {
    if (
      widgetCssBundleForKommunicate &&
      typeof window.Kommunicate?.customizeWidgetCss === 'function'
    ) {
      window.Kommunicate.customizeWidgetCss(widgetCssBundleForKommunicate)
    }
  } catch (error) {
    console.warn('[Fintepla] customizeWidgetCss before reveal failed.', error)
  }

  // Kommunicate's documented visibility API. Called defensively after our
  // overrides are in place so that, even if a previous session hid the
  // widget, the first paint the user sees is our themed launcher.
  try {
    window.Kommunicate?.displayKommunicateWidget?.(true)
  } catch (error) {
    console.warn('[Fintepla] displayKommunicateWidget(true) failed', error)
  }
  debugLog(config, `Widget revealed (reason: ${reason}).`)
}

function syncHeaderContent(doc, config) {
  if (!doc) return

  const titleEl = doc.getElementById(SELECTORS.titleId)
  if (titleEl && titleEl.textContent !== config.headerTitle) {
    titleEl.textContent = config.headerTitle
  }

  const avatarEl = doc.querySelector(SELECTORS.avatar)
  if (avatarEl && avatarEl.getAttribute('src') !== chatbotAvatarUrl) {
    avatarEl.setAttribute('src', chatbotAvatarUrl)
  }
}

function watchHeaderContent(doc, config) {
  if (!doc) return
  syncHeaderContent(doc, config)

  const header = doc.querySelector(SELECTORS.headerBox)
  if (!header) return

  const observer = new MutationObserver(() => syncHeaderContent(doc, config))
  observer.observe(header, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['src'],
  })
}


// Uses the documented Kommunicate Web SDK API: window.Kommunicate.sendMessage.
// Debounced via aria-busy to prevent double-sends on fast clicks.
//
// Analytics: emits `custom_speak_to_person_clicked` with a fresh
// `client_action_id`, then attempts to forward that ID (plus the
// `analytics_session_id`) through Kommunicate.updateChatContext so the
// backend can correlate the next bot turn with the frontend CTA click.
let conversationStartAnnounced = false
function sendSpeakToPersonMessage(button, config, source = 'widget_cta', messageOverride = null) {
  if (button.getAttribute('aria-busy') === 'true') return
  button.setAttribute('aria-busy', 'true')
  window.setTimeout(() => button.removeAttribute('aria-busy'), 750)

  const clientActionId = newClientActionId()
  const analyticsSessionId = getAnalyticsSessionId()

  track('custom_speak_to_person_clicked', {
    correlation: { client_action_id: clientActionId },
    message: messageOverride || {
      button_id: SPEAK_TO_PERSON_BUTTON_ID,
      button_label: config.speakToPersonText,
    },
    metadata: { intent: 'speak_to_person', source },
  })

  if (!conversationStartAnnounced) {
    conversationStartAnnounced = true
    track('conversation_start_requested', {
      correlation: { client_action_id: clientActionId },
      metadata: { trigger: 'speak_to_person' },
    })
  }

  // Forward correlation IDs to Kommunicate so the next message the backend
  // receives can be joined against the frontend event. `updateChatContext`
  // is documented but availability varies by SDK build — swallow silently.
  try {
    window.Kommunicate?.updateChatContext?.({
      analytics_session_id: analyticsSessionId,
      anonymous_user_id: getAnonymousUserId(),
      client_action_id: clientActionId,
      intent: 'speak_to_person',
    })
  } catch (error) {
    console.warn('[Fintepla] updateChatContext failed (non-fatal).', error)
  }

  const payload = config.speakToPersonText
  const api = window.Kommunicate

  if (typeof api?.sendMessage === 'function') {
    try {
      api.sendMessage({ message: payload })
      return
    } catch (error) {
      console.warn('[Fintepla] sendMessage({message}) failed, retrying with string form.', error)
    }

    try {
      api.sendMessage(payload)
      return
    } catch (error) {
      console.warn('[Fintepla] sendMessage(string) failed, falling back to DOM submit.', error)
    }
  }

  const doc = window.KommunicateGlobal?.document
  const input = doc?.getElementById('mck-msg-to') || doc?.querySelector('textarea')
  const form = doc?.getElementById('mck-msg-form')

  if (input && form) {
    input.value = payload
    input.dispatchEvent(new Event('input', { bubbles: true }))
    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))
  } else {
    console.error('[Fintepla] Could not send message — no Kommunicate API and no fallback form.')
  }
}

export function triggerSpeakToPersonAction(
  overrides = {},
  source = 'external_cta',
  messageOverride = null,
) {
  if (typeof window === 'undefined') return false
  const config = resolveConfig(overrides)
  const widgetDoc = window.KommunicateGlobal?.document
  const button = widgetDoc?.getElementById(SPEAK_TO_PERSON_BUTTON_ID)
  if (button) {
    sendSpeakToPersonMessage(button, config, source, messageOverride)
    return true
  }

  // Fallback: if widget chrome is not ready yet, at least open launcher.
  widgetDoc?.getElementById(SELECTORS.launcherId)?.click()
  return false
}

function ensureSpeakToPersonButton(doc, config) {
  if (!doc) return

  const container = doc.getElementById(SELECTORS.footerContainerId)
  if (!container) return
  if (doc.getElementById(SPEAK_TO_PERSON_BUTTON_ID)) return

  const button = doc.createElement('button')
  button.type = 'button'
  button.id = SPEAK_TO_PERSON_BUTTON_ID
  button.className = 'fintepla-speak-to-person'
  button.setAttribute('aria-label', config.speakToPersonText)
  button.textContent = config.speakToPersonText
  button.addEventListener('click', () => sendSpeakToPersonMessage(button, config))

  container.appendChild(button)
}

function watchFooter(doc, config) {
  if (!doc) return
  ensureSpeakToPersonButton(doc, config)

  const container = doc.getElementById(SELECTORS.footerContainerId)
  if (!container) return

  const observer = new MutationObserver(() => ensureSpeakToPersonButton(doc, config))
  observer.observe(container, { childList: true, subtree: false })
}

// Widget lifecycle analytics. Kommunicate's SDK does not expose reliable
// cross-version `onChatWidgetOpen`/`onChatWidgetClose` callbacks, so we
// observe the `#mck-sidebox.in` class transition (the same signal the
// greeting popup already keys off) and emit normalized analytics events.
//
// Also attaches a one-shot capture listener on the launcher for the
// `chat_launcher_clicked` event — this fires whether the user opens the
// chat via pointer or keyboard.
function watchWidgetLifecycle(doc, config) {
  if (!doc) return
  const win = doc.defaultView
  if (!win) return

  const launcher = doc.getElementById(SELECTORS.launcherId)
  if (launcher && launcher.dataset.finteplaAnalyticsBound !== '1') {
    launcher.dataset.finteplaAnalyticsBound = '1'
    const onLauncherActivate = () => {
      track('chat_launcher_clicked', {
        correlation: { client_action_id: newClientActionId() },
      })
    }
    launcher.addEventListener('pointerdown', onLauncherActivate, { capture: true })
    launcher.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter' || event.key === ' ') onLauncherActivate()
      },
      { capture: true },
    )
  }

  const sidebox = doc.getElementById('mck-sidebox')
  if (!sidebox || sidebox.dataset.finteplaAnalyticsBound === '1') return
  sidebox.dataset.finteplaAnalyticsBound = '1'

  let wasOpen = sidebox.classList.contains('in')
  const observer = new win.MutationObserver(() => {
    const isOpen = sidebox.classList.contains('in')
    if (isOpen === wasOpen) return
    wasOpen = isOpen
    if (isOpen) {
      track('chat_widget_opened')
      if (!conversationStartAnnounced) {
        conversationStartAnnounced = true
        track('conversation_start_requested', {
          correlation: { client_action_id: newClientActionId() },
          metadata: { trigger: 'launcher_open' },
        })
      }
    } else {
      // Unload-sensitive: browsers commonly close the widget during tab
      // teardown. `beacon: true` causes the transport to prefer sendBeacon.
      track('chat_widget_closed', {}, { beacon: true })
    }
  })
  observer.observe(sidebox, { attributes: true, attributeFilter: ['class'] })
  debugLog(config, 'Widget open/close analytics observer attached.')
}

// Correlation requirement:
// The backend webhook correlates frontend journeys by reading
// `analytics_session_id` + `client_action_id` from inbound Kommunicate message
// metadata. We can't reliably mutate the outgoing message payload, but we can
// stamp correlation keys into Kommunicate's chat context right before the user
// message is submitted.
function watchUserMessageCorrelation(doc, config) {
  if (!doc) return

  const form = doc.getElementById('mck-msg-form')
  if (!form || form.dataset.finteplaCorrelationBound === '1') return
  form.dataset.finteplaCorrelationBound = '1'

  const onSubmitCapture = () => {
    const clientActionId = newClientActionId()
    try {
      window.Kommunicate?.updateChatContext?.({
        analytics_session_id: getAnalyticsSessionId(),
        anonymous_user_id: getAnonymousUserId(),
        client_action_id: clientActionId,
        intent: 'user_message',
      })
    } catch (error) {
      debugLog(config, 'updateChatContext before user message failed (non-fatal).', error)
    }
  }

  try {
    form.addEventListener('submit', onSubmitCapture, { capture: true })
  } catch {
    // swallow
  }
}

function hardenTranscriptLinks(doc) {
  if (!doc) return

  const container = doc.getElementById('mck-message-cell')
  if (!container) return

  const anchors = container.querySelectorAll('a[href]')
  anchors.forEach((anchor) => {
    const href = anchor.getAttribute('href') || ''
    let isSafe = false
    try {
      const url = new URL(href, 'https://example.com')
      isSafe = url.protocol === 'http:' || url.protocol === 'https:'
    } catch {
      // Keep default false for invalid URLs.
    }

    if (!isSafe) {
      anchor.removeAttribute('href')
      anchor.style.pointerEvents = 'none'
      anchor.setAttribute('aria-disabled', 'true')
      return
    }

    anchor.setAttribute('target', '_blank')
    anchor.setAttribute('rel', 'noopener noreferrer')
  })
}

function watchTranscriptLinks(doc, config) {
  if (!doc) return
  hardenTranscriptLinks(doc)

  const container = doc.getElementById('mck-message-cell')
  if (!container) return

  const observer = new MutationObserver(() => hardenTranscriptLinks(doc))
  observer.observe(container, { subtree: true, childList: true })
  debugLog(config, 'Transcript link hardening observer attached.')
}

function setQuickReplySelected(doc, selected) {
  if (!doc) return
  const sidebox = doc.getElementById('mck-sidebox')
  if (!sidebox) return

  if (selected) {
    sidebox.setAttribute('data-fintepla-quick-reply-selected', 'true')
    return
  }

  sidebox.removeAttribute('data-fintepla-quick-reply-selected')
}

function syncQuickReplySelectedState(doc) {
  if (!doc) return
  const hasQuickReplies = !!doc.querySelector(
    '#mck-message-cell .contains-quick-replies-only button.km-quick-replies',
  )

  if (hasQuickReplies) {
    setQuickReplySelected(doc, false)
  }
}

function watchQuickReplySelection(doc, config) {
  if (!doc) return
  syncQuickReplySelectedState(doc)

  const container = doc.getElementById('mck-message-cell')
  if (!container) return

  const onClick = (event) => {
    const target = event.target
    if (!(target instanceof doc.defaultView.HTMLElement)) return

    const button = target.closest('button.km-quick-replies')
    if (!button) return

    setQuickReplySelected(doc, true)
  }

  container.addEventListener('click', onClick, { capture: true })

  const observer = new MutationObserver(() => syncQuickReplySelectedState(doc))
  observer.observe(container, { subtree: true, childList: true })
  debugLog(config, 'Quick reply selection observer attached.')
}

// Resource strip — two PDF links (Medication Guide / Prescribing Information)
// rendered directly below the chat header and above the scrollable message
// body. Insertion point is `#mck-sidebox-content`, immediately after
// `.mck-box-top` (header). `#mck-sidebox-content` is the flex column that
// holds header → (hidden panels) → `.mck-box-body` → footer as siblings, so
// appending our row between the header and the message body places it where
// the spec requires. Typography, sizing, and the 3px purple rule under the
// strip (box-shadow) are in theme.css.
function buildResourceStrip(doc) {
  const strip = doc.createElement('div')
  strip.id = RESOURCE_STRIP_ID
  strip.className = 'fintepla-resource-strip'
  strip.setAttribute('role', 'group')
  strip.setAttribute('aria-label', 'Fintepla resources')

  const leftLink = RESOURCE_LINKS.find((l) => l.side === 'left')
  const rightLink = RESOURCE_LINKS.find((l) => l.side === 'right')

  const makeColumn = (link) => {
    const col = doc.createElement('div')
    col.className = `fintepla-resource-strip__col fintepla-resource-strip__col--${link.side}`

    const inner = doc.createElement('div')
    inner.className = `fintepla-resource-strip__inner fintepla-resource-strip__inner--${link.side}`

    const anchor = doc.createElement('a')
    anchor.className = 'fintepla-resource-strip__link'
    anchor.href = link.url
    anchor.target = '_blank'
    anchor.rel = 'noopener noreferrer'
    anchor.textContent = link.label

    inner.appendChild(anchor)
    col.appendChild(inner)
    return col
  }

  const divider = doc.createElement('span')
  divider.className = 'fintepla-resource-strip__divider'
  divider.setAttribute('aria-hidden', 'true')

  strip.appendChild(makeColumn(leftLink))
  strip.appendChild(divider)
  strip.appendChild(makeColumn(rightLink))

  return strip
}

function ensureResourceStrip(doc) {
  if (!doc) return

  const container = doc.getElementById(SELECTORS.contentContainerId)
  if (!container) return

  const header = container.querySelector('.mck-box-top')
  if (!header) return

  const existing = doc.getElementById(RESOURCE_STRIP_ID)
  if (existing) {
    if (existing.previousElementSibling !== header) {
      header.insertAdjacentElement('afterend', existing)
    }
    return
  }

  header.insertAdjacentElement('afterend', buildResourceStrip(doc))
}

function watchResourceStrip(doc, config) {
  if (!doc) return
  ensureResourceStrip(doc)

  const container = doc.getElementById(SELECTORS.contentContainerId)
  if (!container) return

  const observer = new MutationObserver(() => ensureResourceStrip(doc))
  observer.observe(container, { childList: true, subtree: false })
  debugLog(config, 'Resource strip observer attached.')
}

function enableKommunicateGreetingPopup(doc, config) {
  if (!doc) return

  const win = doc.defaultView
  if (!win) return

  const sidebox = doc.getElementById('mck-sidebox')
  const launcher = doc.getElementById(SELECTORS.launcherId)
  if (!sidebox || !launcher) return

  const hostIframe = (() => {
    const frames = window.document?.querySelectorAll?.('iframe') || []
    for (const frame of frames) {
      try {
        if (frame.contentDocument === doc) return frame
      } catch {
        // Cross-origin frames are ignored.
      }
    }
    return null
  })()

  const ensurePopupFits = () => {
    if (!hostIframe) return
    const ds = hostIframe.dataset || {}
    if (!ds.finteplaOriginalWidth) {
      ds.finteplaOriginalWidth = hostIframe.style.width || ''
      ds.finteplaOriginalHeight = hostIframe.style.height || ''
    }
    hostIframe.style.width = '370px'
    hostIframe.style.height = '180px'
  }

  const restoreIframeSize = () => {
    if (!hostIframe) return
    const ds = hostIframe.dataset || {}
    hostIframe.style.width = ds.finteplaOriginalWidth || ''
    hostIframe.style.height = ds.finteplaOriginalHeight || ''
  }

  const hidePopup = () => {
    const popup = doc.getElementById('chat-popup-widget-container')
    if (!popup) return
    popup.classList.add('n-vis')
    popup.style.setProperty('display', 'none', 'important')
  }

  const delayMs = Number(config.chatPopupDelayMs)
  const waitMs = Number.isFinite(delayMs) && delayMs > 0 ? delayMs : 12000

  let cancelled = false
  const cancel = () => {
    if (cancelled) return
    cancelled = true
    win.clearTimeout(timeoutId)
    hidePopup()
    restoreIframeSize()
    launcher.removeEventListener('pointerdown', cancel, true)
    launcher.removeEventListener('keydown', onLauncherKey, true)
    try {
      sideObserver.disconnect()
    } catch {
      //
    }
  }

  const onLauncherKey = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      cancel()
    }
  }

  launcher.addEventListener('pointerdown', cancel, { capture: true })
  launcher.addEventListener('keydown', onLauncherKey, { capture: true })

  const sideObserver = new win.MutationObserver(() => {
    if (sidebox.classList.contains('in')) {
      cancel()
    }
  })
  sideObserver.observe(sidebox, { attributes: true, attributeFilter: ['class'] })

  const timeoutId = win.setTimeout(() => {
    if (cancelled) return
    if (sidebox.classList.contains('in')) return

    const popup = doc.getElementById('chat-popup-widget-container')
    if (!popup) return

    ensurePopupFits()

    const wrapper = popup.querySelector('.chat-popup-widget-text-wrapper')
    if (wrapper && !popup.querySelector('#fintepla-greeting-bubble')) {
      const existing = wrapper.querySelectorAll(':scope > *')
      existing.forEach((node) => node.remove())

      const shell = doc.createElement('div')
      shell.id = 'fintepla-greeting-bubble'
      shell.className = 'fintepla-greeting-bubble'

      const bubble = doc.createElement('img')
      bubble.className = 'fintepla-greeting-bubble__img'
      bubble.src = greetingBubbleUrl
      bubble.alt = "Hi, I'm Faye! What can I help you with today?"

      const closeBtn = doc.createElement('button')
      closeBtn.type = 'button'
      closeBtn.className = 'fintepla-greeting-bubble__close'
      closeBtn.setAttribute('aria-label', 'Dismiss greeting')

      const closeImg = doc.createElement('img')
      closeImg.src = closeButtonUrl
      closeImg.alt = ''
      closeBtn.appendChild(closeImg)

      closeBtn.addEventListener('click', (event) => {
        event.stopPropagation()
        hidePopup()
        restoreIframeSize()
      })

      shell.addEventListener('click', () => {
        hidePopup()
        launcher.click()
      })

      shell.appendChild(bubble)
      shell.appendChild(closeBtn)
      wrapper.appendChild(shell)
    }

    popup.classList.remove('n-vis')
    popup.style.setProperty('display', 'block', 'important')
    popup.style.setProperty('visibility', 'visible', 'important')
    popup.style.setProperty('opacity', '1', 'important')
  }, waitMs)
}

// Session can restore the panel as open. Until closed, hide `.in` with a
// short-lived style; click the SDK close control, and drop the guard once the
// user uses the launcher or the cold-start window ends. Idempotent.
const CLOSED_GUARD_FLAG = '__finteplaClosedOnLoadGuard'
const CLOSED_GUARD_RULE_ID = 'fintepla-force-closed-on-load'
const SESSION_AUTOOPEN_GUARD_MS = 16000

function ensureClosedOnLoad(doc, config) {
  if (!doc) return
  if (doc[CLOSED_GUARD_FLAG]) return

  const sidebox = doc.getElementById('mck-sidebox')
  if (!sidebox) return

  doc[CLOSED_GUARD_FLAG] = true

  const styleEl = doc.createElement('style')
  styleEl.id = CLOSED_GUARD_RULE_ID
  styleEl.textContent = `
    #mck-sidebox.in { visibility: hidden !important; opacity: 0 !important; pointer-events: none !important; }
  `
  doc.head.appendChild(styleEl)

  let userOpenedViaLauncher = false
  let finished = false
  let timeoutId = null

  const removeCloak = () => {
    if (styleEl.parentNode) {
      styleEl.remove()
    }
  }

  const tryClose = () => {
    if (userOpenedViaLauncher) return
    if (!sidebox.classList.contains('in')) return

    const closeBtn = doc.getElementById('km-chat-widget-close-button')
    if (!closeBtn) return

    closeBtn.click()
    debugLog(config, 'Auto-closed restored-open chat session on load.')
  }

  const finish = () => {
    if (finished) return
    finished = true
    observer.disconnect()
    if (timeoutId != null) window.clearTimeout(timeoutId)
    removeCloak()
    doc[CLOSED_GUARD_FLAG] = false
  }

  const attachLauncherIntent = () => {
    const launcher = doc.getElementById(SELECTORS.launcherId)
    if (!launcher || launcher.dataset.finteplaUserLaunchBound === '1') return
    launcher.dataset.finteplaUserLaunchBound = '1'

    const markUserLaunch = () => {
      userOpenedViaLauncher = true
      removeCloak()
    }

    launcher.addEventListener('pointerdown', markUserLaunch, { capture: true })
    launcher.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          markUserLaunch()
        }
      },
      { capture: true },
    )
  }

  const bump = () => {
    if (finished) return
    attachLauncherIntent()
    if (userOpenedViaLauncher) return
    if (sidebox.classList.contains('in')) {
      tryClose()
    }
  }

  const observer = new MutationObserver(bump)
  observer.observe(sidebox, { attributes: true, attributeFilter: ['class'] })

  attachLauncherIntent()
  bump()

  timeoutId = window.setTimeout(finish, SESSION_AUTOOPEN_GUARD_MS)
}

function loadKommunicateScript(config, widgetCss) {
  if (document.querySelector(`script[src="${KOMMUNICATE_SCRIPT_URL}"]`)) {
    debugLog(config, 'Kommunicate SDK script already present; skipping re-injection.')
    return
  }

  // Merge preRegistered (e.g. video-dispatcher) into settings; see window.kommunicate._globals.
  const preRegistered =
    (typeof window !== 'undefined' && window.kommunicateSettings) || {}

  const mergedAppSettings = {
    ...(preRegistered.appSettings && typeof preRegistered.appSettings === 'object'
      ? preRegistered.appSettings
      : {}),
  }

  const mergedChatWidget = {
    ...(mergedAppSettings.chatWidget && typeof mergedAppSettings.chatWidget === 'object'
      ? mergedAppSettings.chatWidget
      : {}),
    popup: true,
  }

  mergedAppSettings.chatWidget = mergedChatWidget

  if (!Array.isArray(mergedAppSettings.chatPopupMessage)) {
    mergedAppSettings.chatPopupMessage = [
      {
        message: config.chatPopupMessageText,
        delay: config.chatPopupDelayMs,
      },
    ]
  }

  const kommunicateSettings = {
    appId: config.appId,
    popupWidget: false,
    automaticChatOpenOnNavigation: false,
    restartConversationByUser: true,
    appSettings: mergedAppSettings,

    ...(typeof preRegistered.onRichMessageLinkClick === 'function'
      ? { onRichMessageLinkClick: preRegistered.onRichMessageLinkClick }
      : {}),
    ...(typeof preRegistered.onMessageReceived === 'function'
      ? { onMessageReceived: preRegistered.onMessageReceived }
      : {}),

    onInit() {
      debugLog(config, 'kommunicateSettings.onInit fired.')
      const { Kommunicate } = window
      try {
        Kommunicate?.customizeWidgetCss?.(widgetCss)
        debugLog(config, 'customizeWidgetCss applied.')

        revealWidget(config, 'onInit')

        Kommunicate?.updateSettings?.({
          defaultBotIds: [config.botId],
          defaultAssignee: config.botId,
          skipRouting: true,
          conversationTitle: config.headerTitle,
        })

        // Seed correlation IDs into the bot conversation context up front
        // so every outbound message carries them server-side, even before
        // the user interacts with a custom CTA. Optional-chain: the method
        // is documented but not present in every SDK build we might load.
        try {
          Kommunicate?.updateChatContext?.({
            analytics_session_id: getAnalyticsSessionId(),
            anonymous_user_id: getAnonymousUserId(),
          })
        } catch (error) {
          console.warn('[Fintepla] updateChatContext seed failed (non-fatal).', error)
        }

        track('chat_widget_loaded')

        const initDoc = window.KommunicateGlobal?.document
        if (initDoc) ensureClosedOnLoad(initDoc, config)
      } catch (error) {
        console.error('[Fintepla] Failed to apply Kommunicate theme/settings.', error)
      } finally {
        revealWidget(config, 'onInit-fallback')
      }
    },
  }

  // Chain `onRichMessageButtonClick` for button-shaped rich messages. The
  // same chaining policy as `onRichMessageLinkClick` applies: never stomp a
  // pre-registered handler, and never let our logic break Kommunicate's
  // flow. Not every SDK version fires this callback — when it does, we
  // normalize a minimum envelope for the backend.
  const preRichButton =
    typeof preRegistered.onRichMessageButtonClick === 'function'
      ? preRegistered.onRichMessageButtonClick
      : null
  kommunicateSettings.onRichMessageButtonClick = function (...args) {
    try {
      const payload =
        args.find(
          (a) => a && typeof a === 'object' && ('action' in a || 'label' in a || 'name' in a),
        ) || {}
      track('rich_message_button_clicked', {
        correlation: { client_action_id: newClientActionId() },
        message: {
          button_id: payload.id || payload.buttonId || undefined,
          button_label: payload.label || payload.name || payload.title || undefined,
          message_id: payload.messageKey || payload.messageId || undefined,
        },
        metadata: {
          action: typeof payload.action === 'string' ? payload.action : undefined,
        },
      })
    } catch (err) {
      console.warn('[Fintepla] rich_message_button_clicked track failed', err)
    }
    if (preRichButton) {
      try {
        return preRichButton.apply(this, args)
      } catch (err) {
        console.warn('[Fintepla] chained onRichMessageButtonClick threw', err)
      }
    }
    return undefined
  }

  window.kommunicate = window.kommunicate || {}
  window.kommunicate._globals = kommunicateSettings
  // Also expose as `window.kommunicateSettings` so any SDK build that
  // reads the documented global name (rather than the `_globals`
  // convention) sees the same merged config — and so a late-loading
  // script can still chain onto our handlers if needed.
  window.kommunicateSettings = kommunicateSettings

  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.async = true
  script.src = KOMMUNICATE_SCRIPT_URL
  script.addEventListener('load', () => {
    debugLog(config, 'Kommunicate SDK script loaded.')
  })
  script.addEventListener('error', (event) => {
    console.error('[Fintepla] Failed to load Kommunicate script.', event)
    // Reveal anyway — keeping the host hidden would be the worst failure
    // mode (invisible iframe + no error path to the user).
    revealWidget(config, 'script-error')
  })
  document.head.appendChild(script)
  debugLog(config, 'Kommunicate SDK script tag appended to <head>.')
}

// The Kommunicate SDK does not emit a public "iframe DOM ready" event, so
// we poll on requestAnimationFrame (paint-aligned, cheap) with a hard
// timeout until the launcher exists inside `KommunicateGlobal.document`.
// This is used only to attach our MutationObservers for the header-title /
// avatar / Speak-to-Person button augmentation — NOT for styling, which
// has already been applied synchronously inside `onInit`.
function whenWidgetDomReady(onReady, config) {
  const deadline = performance.now() + READY_TIMEOUT_MS

  const tick = () => {
    const doc = window.KommunicateGlobal?.document
    const launcher = doc?.getElementById(SELECTORS.launcherId)
    if (launcher) {
      onReady()
      return
    }
    if (performance.now() > deadline) {
      console.error(
        '[Fintepla] Kommunicate widget DOM did not become ready within',
        READY_TIMEOUT_MS,
        'ms',
      )
      return
    }
    requestAnimationFrame(tick)
  }

  requestAnimationFrame(tick)
  debugLog(config, 'Waiting for Kommunicate widget DOM…')
}

// ---------------------------------------------------------------------------
// Public entry point. Idempotent: safe to call on every SPA route change;
// the first call wins and subsequent calls are no-ops. To fully tear down
// for testing, delete window[INIT_FLAG] and remove the SDK <script>.
// ---------------------------------------------------------------------------
export function setupKommunicateUi(overrides = {}) {
  if (typeof window === 'undefined') return

  if (window[INIT_FLAG]) {
    return
  }
  window[INIT_FLAG] = true

  const config = resolveConfig(overrides)
  // Idempotent: safe when embed.js already called this with the same cfg
  // (IIFE path) and also the first-and-only call from the Vite playground
  // path (src/main.js).
  configureAnalytics(config)
  const widgetCss = buildWidgetCss(config)
  widgetCssBundleForKommunicate = widgetCss
  const concealMaxMsRaw = Number(config.concealMaxMs)
  const concealMaxMs =
    Number.isFinite(concealMaxMsRaw) && concealMaxMsRaw > 0 ? concealMaxMsRaw : 1000

  // Order matters: prehide BEFORE script injection so no frame of default
  // Kommunicate styling is ever visible.
  installPrehideStyle()
  loadKommunicateScript(config, widgetCss)

  // Cap how long we stay hidden: slow networks can delay `onInit` for many
  // seconds. We still prefer revealing on `onInit` as soon as it runs; this
  // only fires if we are still cloaked at the deadline (then theme applies
  // when `onInit` arrives). If `onInit` already revealed, this is a no-op.
  window.setTimeout(() => {
    if (document.documentElement.classList.contains(LOADING_CLASS)) {
      console.warn(
        '[Fintepla] Kommunicate onInit had not revealed the widget within',
        concealMaxMs,
        'ms — showing launcher now. Theme applies when the SDK finishes init.',
      )
      revealWidget(config, 'conceal-cap')
    }
  }, concealMaxMs)

  whenWidgetDomReady(() => {
    const doc = window.KommunicateGlobal?.document
    ensureClosedOnLoad(doc, config)
    watchHeaderContent(doc, config)
    watchFooter(doc, config)
    watchTranscriptLinks(doc, config)
    watchQuickReplySelection(doc, config)
    watchResourceStrip(doc, config)
    watchWidgetLifecycle(doc, config)
    watchUserMessageCorrelation(doc, config)
    enableKommunicateGreetingPopup(doc, config)
    debugLog(config, 'Widget chrome customizations attached.')
  }, config)
}
